import { IMicroFEGuest } from './typings/guest.js';
import { EMUI } from './typings/window.js';

export class CAppRegistry {
  constructor() {
    if (!window.emui) {
      window.emui = {} as EMUI;
    }
    window.emui.registerApp = this.#registerApp;
  }

  /**
   * global method for guest apps to register themselves with parent
   * @param param0
   * @param param0.appId
   * @param param0.app
   */
  // eslint-disable-next-line complexity
  #registerApp = ({ appId, app }: { appId: string; app: IMicroFEGuest }) => {
    if (!appId) throw new Error('appId is required');
    if (
      appId === '__proto__' ||
      appId === 'constructor' ||
      appId === 'prototype'
    ) {
      throw new Error(`Invalid appId: ${appId}`);
    }
    if (!app?.uuid) throw new Error('application uuid is required');
    window.emui[appId] = window.emui[appId] || [];
    if (Array.isArray(window.emui[appId])) {
      const appIndex = window.emui[appId].findIndex(
        (eApp) => eApp.uuid === app.uuid,
      );
      if (appIndex > -1) {
        window.emui[appId][appIndex] = app;
      } else {
        window.emui[appId].push(app);
      }
    } else {
      let existingApp = window.emui[appId];
      if (existingApp?.uuid === app.uuid) {
        existingApp = { ...existingApp, ...app };
        window.emui[appId] = [existingApp];
      } else if (typeof existingApp.init === 'undefined') {
        window.emui[appId] = [{ ...existingApp, ...app }];
      } else {
        window.emui[appId] = [window.emui[appId], app];
      }
    }
  };

  /**
   * get micro app guest from window object
   * @param param0 id and uuid
   * @param param0.id app id
   * @param param0.uuid unique instance id
   * @param param0.instanceId
   * @returns micro app guest
   */
  get = ({ id, instanceId }: { id: string; instanceId: string }) => {
    const apps = window.emui?.[id];
    if (Array.isArray(apps)) {
      // more than one instance of the same app
      const app = apps.find((eApp) => eApp.uuid === instanceId);
      return app ?? null;
    }
    if (apps?.uuid === instanceId) {
      return apps;
    }
    return null;
  };

  /**
   * add app to global emui window variable
   * @param id.id
   * @param id app id
   * @param instanceId unique instance id
   * @param id.instanceId
   * @param id.documentEle
   */
  add = ({
    id,
    instanceId,
    documentEle,
  }: {
    id: string;
    instanceId: string;
    documentEle: Document;
  }) => {
    if (!instanceId) {
      throw new Error(
        `instanceId (uuid) is required to register application ${id}`,
      );
    }
    const newAppInstance = {
      uuid: instanceId,
      init: null,
      mount: null,
      unmount: null,
    } as unknown as Omit<IMicroFEGuest, 'guestWindow'>;
    const appInstances = window.emui[id];
    if (appInstances) {
      if (Array.isArray(appInstances)) {
        appInstances.push(newAppInstance);
      } else {
        window.emui[id] = [appInstances, newAppInstance];
      }
    } else {
      window.emui[id] = [newAppInstance];
    }
    if (documentEle.defaultView) {
      documentEle.defaultView.emui = documentEle.defaultView.emui ?? {};
      (
        documentEle.defaultView.emui as unknown as Omit<
          IMicroFEGuest,
          'guestWindow'
        >
      ).uuid = instanceId;
    }
  };

  /**
   * delete app from global emui window variable
   * @param id.id
   * @param id app id
   * @param instanceId unique instance id
   * @param id.instanceId
   */
  delete = ({ id, instanceId }: { id: string; instanceId: string }) => {
    if (Array.isArray(window.emui[id])) {
      const index = window.emui[id].findIndex(
        (app: Omit<IMicroFEGuest, 'guestWindow'>) => app.uuid === instanceId,
      );
      if (index > -1) window.emui[id].splice(index, 1);
      if (window.emui[id].length === 0) delete window.emui[id];
    } else {
      delete window.emui[id];
    }
  };
}
